import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { getRegionIdFromCookie, setRegionCookies } from './utils/cookie';
import { EGeoSwitcherStatus, IGeoSwitcherState, IRegion } from '../../types/geoSwitcher';
import { IApplicationState } from '../../types/redux';
import { getPopularCities, getSuggestions, getRegion, setCurrentRegion } from '../../actions/geoSwitcher';
import { trackGeoSwitcherClick } from './tracking';
import { GeoSwitcherHeader } from '../../components/GeoSwitcher/components/GeoSwitcherHeader';
import { GeoSwitcherBody } from '../../components/GeoSwitcher/components/GeoSwitcherBody';
import { GeoSwitcher } from '../../components/GeoSwitcher';
import { useApplicationContext } from '../../reactContext/applicationContext';

export const GeoSwitcherContainer = () => {
  const dispatch = useDispatch();
  const context = useApplicationContext();
  const [inputValue, setInputValue] = React.useState<string>('');
  const { status, region, popularCities, suggests } = useSelector<IApplicationState, IGeoSwitcherState>(
    state => state.geoSwitcher,
  );

  const debounceGetSuggestions = React.useCallback(
    debounce(value => {
      dispatch(getSuggestions(value));
    }, 300),
    [dispatch],
  );

  React.useEffect(() => {
    const regionIdFromCookie = getRegionIdFromCookie();
    dispatch(getRegion(regionIdFromCookie));
    dispatch(getPopularCities());
  }, []);

  React.useEffect(() => {
    if (region) {
      setInputValue(region.displayName);
      debounceGetSuggestions(region.displayName);
    }
  }, [region, debounceGetSuggestions]);

  const onChangeInputValue = React.useCallback(
    (value: string) => {
      setInputValue(value);
      debounceGetSuggestions(value);
    },
    [debounceGetSuggestions],
  );

  const onChangeRegion = React.useCallback(
    (region: IRegion) => {
      dispatch(setCurrentRegion(region));
      debounceGetSuggestions(region.displayName);

      context.telemetry.increment('geoSwitcher.changeRegion');
    },
    [dispatch, debounceGetSuggestions],
  );

  const onSelectRegion = React.useCallback(
    (selectedRegion: IRegion) => {
      setInputValue(selectedRegion.displayName);
      dispatch(setCurrentRegion(selectedRegion));
      debounceGetSuggestions(selectedRegion.displayName);
    },
    [dispatch, debounceGetSuggestions],
  );

  const onSubmitRegion = React.useCallback(() => {
    if (region) {
      dispatch(getRegion(region.id));
      setRegionCookies(region);

      context.telemetry.increment('geoSwitcher.selectRegion');
    }
  }, [region, dispatch]);

  return popularCities.length > 0 && region ? (
    <GeoSwitcher
      header={
        <GeoSwitcherHeader
          inputValue={inputValue}
          suggests={suggests}
          loading={status === EGeoSwitcherStatus.Loading}
          changeInputValue={onChangeInputValue}
          selectRegion={onSelectRegion}
          submitRegion={onSubmitRegion}
        />
      }
      body={<GeoSwitcherBody popularCities={popularCities} onChangeRegion={onChangeRegion} />}
      regionTitle={region.displayName}
      onOpen={trackGeoSwitcherClick}
    />
  ) : null;
};
